import * as preact from "preact";
import * as _ from "underscore";
import XRegExp from "xregexp";

import * as accountModal from "accountModal";
import models from "models/models";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as smartDictionaryUtils from "smartDictionaryUtils";

interface Props {
  word: string;
  context: string;
  language: string;
  showInFirstLanguage: boolean;
  cacheOnly?: boolean;
  showExplanationButton?: boolean;
  existingTranslations: string[];
  onSelectTranslation?: (translation: string) => void;
  onExplanationFetched?: () => void;
  onClickGenerate?: () => void;
}

interface State {
  status: "loading" | "generated" | "error" | "not-found" | "generating";
  explanation?: string;
  reachedLimit?: boolean;
  fetchTimeoutTimer: number | null;
}

const createRequestData = (props: Props) => ({
  query: props.word,
  context: props.context,
  language: props.language,
  explanationLanguage: props.showInFirstLanguage
    ? models.user.get("firstLanguage")
    : props.language,
});

export default class ExplanationView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      status: "loading",
      fetchTimeoutTimer: null,
    };

    setTimeout(() => {
      this.fetchExplanation();
    });
  }

  explanationLanguage() {
    return this.props.showInFirstLanguage
      ? models.user.get("firstLanguage")
      : this.props.language;
  }

  startFetchTimeoutTimer() {
    /** Fetching timeout threshold (ms) */
    const timeoutPeriod = 10 * 1000;

    this.stopFetchTimeoutTimer();
    this.setState({
      fetchTimeoutTimer: setTimeout(() => {
        this.setState({ status: "error" });
      }, timeoutPeriod),
    });
  }

  stopFetchTimeoutTimer() {
    if (this.state.fetchTimeoutTimer) {
      clearTimeout(this.state.fetchTimeoutTimer);
      this.setState({ fetchTimeoutTimer: null });
    }
  }

  isRequestDataStillValid(requestData) {
    return (
      JSON.stringify(requestData) ===
      JSON.stringify(createRequestData(this.props))
    );
  }

  async fetchExplanation() {
    if (!this.props.cacheOnly) {
      await this.setState({ status: "generating" });
    }

    const requestData = createRequestData(this.props);
    const cachedExplanation = smartDictionaryUtils.cacheGet(requestData);

    if (cachedExplanation) {
      await this.setState({
        status: "generated",
        explanation: cachedExplanation,
      });

      // Check that request data is still the same
      if (!this.isRequestDataStillValid(requestData)) {
        // If not, refetch
        this.fetchExplanation();
        return;
      }

      this.props.onExplanationFetched?.();
      return;
    }

    if (
      this.state.status === "generated" ||
      this.state.status === "error" ||
      this.state.status === "not-found"
    ) {
      // Only auto-refresh if the cacheOnly prop is not set, otherwise it'll refetch on every
      // keystroke within EditCardView
      this.setState({
        status: "not-found",
      });
      return;
    }

    this.setState({ status: "loading" });

    if (!this.props.cacheOnly && window.fetch) {
      this.setState({ status: "generating", explanation: "" });

      this.startFetchTimeoutTimer();
      fetch(
        `/api/smartDictionary?query=${encodeURIComponent(
          requestData.query
        )}&context=${encodeURIComponent(
          requestData.context
        )}&language=${encodeURIComponent(
          requestData.language
        )}&explanationLanguage=${requestData.explanationLanguage}`
      )
        .then((response) => {
          if (response?.ok === false) {
            if (response.status === 403) {
              // To be sure that the user has reached the limit, we could also check the response
              // text, but for simplicity we're going to assume that a 403 here means that they've
              // hit the daily limit.
              this.setState({ status: "error", reachedLimit: true });
            }
            this.setState({ status: "error" });
            return;
          }

          if (!response.ok) {
            this.defineErrorHandler(response);
            this.stopFetchTimeoutTimer();
            return;
          }
          this.startFetchTimeoutTimer();

          const reader = response.body.getReader();
          const processStream = () =>
            reader.read().then(async ({ done, value }) => {
              if (!this.isRequestDataStillValid(requestData)) {
                // If not, stop processing the stream
                return;
              }

              if (done) {
                this.setState({ status: "generated" });
                smartDictionaryUtils.cacheSet(
                  requestData,
                  this.state.explanation
                );
                this.stopFetchTimeoutTimer();
                this.props.onExplanationFetched?.();
                return;
              }
              this.startFetchTimeoutTimer();

              // Decode the received bytes and update the UI
              const decoded = new TextDecoder("utf-8").decode(value);
              if (decoded !== undefined) {
                await this.setState({
                  explanation: this.state.explanation + decoded,
                });
              }

              // Continue processing the stream
              return processStream();
            });

          return processStream();
        })
        .catch((error) => {
          this.defineErrorHandler(error);
          console.error("ERROR: ", error);
          this.setState({
            status: "error",
          });
        });
    } else {
      readlangClient.request(
        "GET",
        "/api/smartDictionary",
        {
          ...requestData,
          cacheOnly: this.props.cacheOnly,
        },
        (explanation) => {
          smartDictionaryUtils.cacheSet(requestData, explanation);
          this.setState({ status: "generated", explanation });
          this.props.onExplanationFetched?.();
        },
        (error) => {
          if (error.status === 403 && error.data === "definitionLimit") {
            this.setState({
              status: "error",
              reachedLimit: true,
            });
          } else {
            this.setState({ status: "not-found" });
          }
        },
        { dataType: "text" }
      );
    }
  }

  defineErrorHandler(error: any) {
    if (error.status === 403) {
      if (typeof error.text === "function") {
        error
          .text()
          .then((text) => {
            if (text === "definitionLimit") {
              this.setState({
                reachedLimit: true,
              });
            }
          })
          .catch((error) => {
            console.error("error.text error: ", error);
          });
        return;
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      this.props.cacheOnly !== prevProps.cacheOnly ||
      JSON.stringify(createRequestData(this.props)) !==
        JSON.stringify(createRequestData(prevProps))
    ) {
      this.fetchExplanation();
    }
  }

  render() {
    return (
      <div class="explanation">
        {(() => {
          switch (this.state.status) {
            case "loading":
              return this.props.cacheOnly ? null : (
                <p>Generating explanation...</p>
              );

            case "error": {
              if (this.state.reachedLimit) {
                return this.props.cacheOnly ? null : (
                  <p>
                    You've reached your daily limit of{" "}
                    {subscriptionPlans.permissions["free"].definitions}{" "}
                    context-aware explanations. Get unlimited explanations by{" "}
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.upgrade();
                      }}
                    >
                      upgrading to premium
                    </a>
                    .
                  </p>
                );
              }

              return this.props.cacheOnly ? null : (
                <p>
                  Problem generating explanation.
                  <button
                    class="linkButton explain"
                    onClick={() => {
                      this.fetchExplanation();
                    }}
                  >
                    Try again
                  </button>
                </p>
              );
            }

            case "generating":
              if (this.state.explanation === undefined) {
                return <p>Generating...</p>;
              }

            // Intentional fallthrough to "generated" to show partial result with blinking
            // cursor...
            // eslint-disable-next-line no-fallthrough
            case "generated":
              return (
                <p
                  style={
                    languageMap.map[this.explanationLanguage()].direction ===
                    "RTL"
                      ? { direction: "rtl" }
                      : {}
                  }
                >
                  <i class="fa icon-lightbulb"></i> &nbsp;{" "}
                  {this.renderExplanation()}
                  {this.state.status === "generating" ? (
                    <span class="blinkingCursor">▌</span>
                  ) : null}
                </p>
              );

            case "not-found": {
              if (this.props.showExplanationButton) {
                return (
                  <button
                    class="linkButton explain"
                    onClick={() => {
                      this.props.onClickGenerate();
                    }}
                  >
                    Explain
                  </button>
                );
              }
              return null;
            }

            default:
              return null;
          }
        })()}
      </div>
    );
  }

  renderExplanation() {
    const explanation = this.state.explanation;

    const parts = convertTextToParts(
      explanation,
      // Don't add buttons for these existing strings
      [this.props.word, ...this.props.existingTranslations],
      this.props.onSelectTranslation ? true : false
    );

    return parts.map((section) => {
      switch (section.type) {
        case "normal": {
          return <span style={{ whiteSpace: "pre-line" }}>{section.text}</span>;
        }
        case "englishTranslation": {
          return (
            <button
              onClick={() => {
                this.props.onSelectTranslation?.(section.text);
              }}
            >
              <i class="icon icon-plus"></i> {section.text}
            </button>
          );
        }
        case "bold": {
          return (
            <strong style={{ whiteSpace: "pre-line" }}>{section.text}</strong>
          );
        }
      }
    });
  }

  upgrade() {
    accountModal.show({
      model: models.user,
      title: "explanationsLimit",
      messageHtml:
        "<div>" +
        "<h2>Upgrade for more than " +
        subscriptionPlans.permissions["free"].definitions +
        " explanations per day</h2>" +
        "<p>Really understand what you're reading with unlimited AI-based " +
        "context-aware explanations!</p>" +
        "</div>",
      showTitle: false,
      smartDefinitions: true,
    });
  }
}

type ExplanationPart = {
  type: "normal" | "englishTranslation" | "bold";
  text: string;
};

const convertTextToParts = (
  input: string,
  exclude: string[],
  detectTranslations: boolean
): ExplanationPart[] => {
  const excludeNormalized = exclude.map((translation) =>
    practiceUtils.normalize(translation)
  );

  const patterns = [
    { before: "can mean " },
    { before: "means " },
    { before: "meaning is " },
    { before: " or " },
    { before: ", ", after: ", " },
    { after: " in English" },
    { before: "translated to " },
    { before: "translated as " },
    { before: "translates to " },
    { before: "translated to English as " },
  ];

  let parts: ExplanationPart[] = [{ type: "normal", text: input }];

  if (detectTranslations) {
    for (const pattern of patterns) {
      parts = _.flatten(
        parts.map((part) => {
          switch (part.type) {
            case "normal": {
              return detectEnglishTranslationsFromPattern(
                part.text,
                pattern,
                excludeNormalized
              );
            }

            default:
              return [part];
          }
        })
      );
    }
  }

  parts = _.flatten(
    parts.map((part) => {
      switch (part.type) {
        case "normal": {
          return detectBoldText(part.text);
        }

        default:
          return [part];
      }
    })
  );

  return parts;
};

const detectEnglishTranslationsFromPattern = (
  input: string,
  pattern: { before?: string; after?: string },
  excludeNormalized: string[]
): ExplanationPart[] => {
  const parts: ExplanationPart[] = [];
  const quotedWord = `["“](\\p{L}[^"”“]+\\p{L})[\\.!?]?["”]`;
  const regExp = XRegExp(
    `${pattern.before || ""}${quotedWord}${pattern.after || ""}`,
    "g"
  );
  let match = regExp.exec(input);

  let processedUpToIndex = 0;

  /**
   * Used to exclude any strings which are much longer than the longest currently included
   * excluded strings
   */
  const longestExcludeLength = excludeNormalized.reduce(
    (longestLength, current) =>
      current.length > longestLength ? current.length : longestLength,
    0
  );

  while (match) {
    if (
      !match[1] ||
      excludeNormalized.includes(practiceUtils.normalize(match[1])) ||
      match[1].length > 3 * longestExcludeLength
    ) {
      // Don't add button
      parts.push({
        type: "normal",
        text: input.substring(
          processedUpToIndex,
          match.index + match[0].length
        ),
      });
      processedUpToIndex = match.index + match[0].length;
    } else {
      // Add button
      parts.push({
        type: "normal",
        text: input.substring(
          processedUpToIndex,
          match.index + (pattern.before?.length ?? 0)
        ),
      });
      parts.push({
        type: "englishTranslation",
        text: match[1],
      });
      // TODO: Add end of sentence punctuation (.!?) if it appears within the quotes
      if (pattern.after) {
        parts.push({
          type: "normal",
          text: pattern.after,
        });
      }
      processedUpToIndex = match.index + match[0].length;
    }

    // Call again, which will start the search at the point in the input string where the last one
    // left off
    match = regExp.exec(input);
  }

  // Add the last section
  parts.push({
    type: "normal",
    text: input.substring(processedUpToIndex),
  });

  return parts;
};

const detectBoldText = (input: string): ExplanationPart[] => {
  const regExp = XRegExp("\\*\\*([^\\*]+)\\*\\*", "g");
  let match = regExp.exec(input);

  const parts = [];

  let processedUpToIndex = 0;

  while (match) {
    parts.push({
      type: "normal",
      text: input.substring(processedUpToIndex, match.index),
    });
    parts.push({
      type: "bold",
      text: match[1],
    });
    processedUpToIndex = match.index + match[0].length;

    // Call again, which will start the search at the point in the input string where the last one
    // left off
    match = regExp.exec(input);
  }

  // Add the last section
  parts.push({
    type: "normal",
    text: input.substring(processedUpToIndex),
  });

  return parts;
};
